import React from "react";
import { Box, Typography } from "@mui/material";
import { useToggleRefinement } from "react-instantsearch";
import { CustomSwitch, directoryFiltersStyles } from "../../pages/styles";

/**
 * Represents a single filter option.
 * @property attribute - The attribute name used for filtering.
 * @property label - The display label for the filter.
 */
interface Filter {
  attribute: string;
  label: string;
}

/**
 * Represents a group of filters of a certain type.
 * @property title - The display title for the filter group.
 * @property component - The type of component used for the filter group.
 * @property filters - The list of filter options in this group.
 */
interface FilterType {
  title: string;
  component: "toggle" | "refinement";
  filters: Filter[];
}

/**
 * Configuration map for all filter types and their options.
 * The keys correspond to filter categories, and the values define
 * the display and filter options for each category.
 *
 * NOTE: Could be moved to a separate file.
 */
const filterTypesMap: Record<string, FilterType> = {
  discipline: {
    title: "Discipline",
    component: "toggle",
    filters: [
      { attribute: "engineering", label: "Engineering" },
      { attribute: "design", label: "Design" },
      { attribute: "materials-science", label: "Materials Science" },
      { attribute: "ecology", label: "Ecology" },
      { attribute: "sustainability", label: "Sustainability" },
      { attribute: "chemistry", label: "Chemistry" },
      { attribute: "biology", label: "Biology" },
      { attribute: "data-science", label: "Data Science" },
      { attribute: "fine-art", label: "Fine Art" },
    ],
  },
  industry: {
    title: "Industry",
    component: "toggle",
    filters: [
      { attribute: "textiles-fashion", label: "Textiles & Fashion" },
      { attribute: "architecture", label: "Architecture" },
      { attribute: "packaging", label: "Packaging" },
      { attribute: "consumer-goods", label: "Consumer Goods" },
      { attribute: "composting-recycling", label: "Composting & Recycling" },
      { attribute: "built-environment", label: "Built Environment" },
      { attribute: "construction", label: "Construction" },
      { attribute: "plastics", label: "Plastics" },
    ],
  },
};

/**
 * CustomToggleRefinement
 *
 * Renders a toggle switch for a given filter attribute and label.
 * Integrates with Algolia's InstantSearch via useToggleRefinement.
 *
 * @param attribute - The attribute to refine on.
 * @param label - The label to display next to the toggle.
 */
const CustomToggleRefinement = ({
  attribute,
  label,
}: {
  attribute: string;
  label: string;
}) => {
  // useToggleRefinement provides the current refinement state and a function to toggle it
  const { value, refine } = useToggleRefinement({
    attribute,
  });

  return (
    <Box sx={directoryFiltersStyles.toggleContainer}>
      <Typography variant="body2">{label}</Typography>
      {/* iOS-style switch toggles the refinement for this attribute */}
      <CustomSwitch
        checked={value.isRefined}
        onChange={() => refine(value)}
        focusVisibleClassName=".Mui-focusVisible"
        disableRipple
      />
    </Box>
  );
};

/**
 * DirectoryFilters Component
 *
 * Displays refinement filters for the user directory search.
 * Uses Algolia's InstantSearch refinement components with dynamic configuration.
 * Renders filter groups and their respective toggle switches.
 */
const DirectoryFilters: React.FC = () => {
  return (
    <Box sx={directoryFiltersStyles.container}>
      {/* Iterate over each filter group defined in filterTypesMap */}
      {Object.entries(filterTypesMap).map(([typeKey, filterType]) => (
        <Box key={typeKey} sx={directoryFiltersStyles.filterGroup}>
          {/* Render the group title */}
          <Typography
            variant="subtitle2"
            sx={directoryFiltersStyles.filterGroupTitle}
          >
            {filterType.title}
          </Typography>
          {/* Render each filter option as a toggle */}
          {filterType.filters.map((filter) => (
            <CustomToggleRefinement
              key={filter.attribute}
              attribute={filter.attribute}
              label={filter.label}
            />
          ))}
        </Box>
      ))}

      {/* Additional filters can be added to the filterTypesMap above */}
    </Box>
  );
};

export default DirectoryFilters;
